import { useEffect, useRef, useState } from 'react'
import TabBar from './components/TabBar.jsx'
import DailyHuddleScreen from './components/DailyHuddleScreen.jsx'
import InventoryHubScreen, { STOCK_SUBS } from './components/InventoryHubScreen.jsx'
import ScheduleHubScreen, { SCHEDULE_SUBS } from './components/ScheduleHubScreen.jsx'
import WeeklyInsightsScreen from './components/WeeklyInsightsScreen.jsx'
import PromotionsScreen from './components/PromotionsScreen.jsx'
import SettingsScreen from './components/SettingsScreen.jsx'
import LoginScreen from './components/LoginScreen.jsx'
import PasswordResetScreen from './components/PasswordResetScreen.jsx'
import SquareCallbackHandler from './components/SquareCallbackHandler.jsx'
import OperatorQueueScreen from './components/OperatorQueueScreen.jsx'
import GroupDashboardScreen from './components/GroupDashboardScreen.jsx'
import SelectUnitPrompt from './components/SelectUnitPrompt.jsx'
import UnitSwitcher from './components/UnitSwitcher.jsx'
import Toast from './components/Toast.jsx'
import { UnitProvider } from './contexts/UnitContext.jsx'
import { useCurrentUnit } from './hooks/useCurrentUnit.js'
import { useSession } from './hooks/useSession.js'
import { useIsOperator } from './hooks/useOperator.js'
import { parseBriefDeepLink } from './lib/briefDeepLink.js'

// App-level shell. Owns the amber-brand header + tab bar, then renders
// the active screen below. Six top-level tabs:
//   Huddle (default), Stock, Schedule, Insights, Marketing, Settings.
//
// Stock and Schedule are hubs that hold sub-screens behind a sub-tab
// pill nav. The pill nav lives INSIDE the sticky amber-brand header so it
// stays visible when the user scrolls within a sub-screen — much
// better than the original placement (where it scrolled away with
// the page content and the manager would lose track of which sub
// they were on).
//
// Auth gate: when there's no Supabase session, LoginScreen renders
// instead of the tab UI. Password recovery deeplink puts us into
// PasswordResetScreen until the new password is saved.
export default function App() {
  const { session, loading, authEvent, clearAuthEvent } = useSession()

  // --- Pre-auth gates (kept in the outer shell so the provider only
  //     mounts for an authenticated user). ---
  if (loading) {
    return (
      <div className="min-h-full bg-white">
        <header className="bg-amber-brand-50 text-amber-brand-800 safe-top">
          <div className="mx-auto max-w-screen-sm px-4 py-4 flex items-center gap-3">
            <img
              src="/logo.png"
              alt=""
              className="h-10 w-10 rounded-lg shadow-md flex-shrink-0"
            />
            <div className="text-xs font-medium opacity-90">
              AI Restaurant Manager
            </div>
          </div>
        </header>
      </div>
    )
  }

  if (authEvent === 'PASSWORD_RECOVERY') {
    return <PasswordResetScreen onDone={clearAuthEvent} />
  }

  if (!session) {
    return <LoginScreen />
  }

  // Square OAuth round-trip: when Square redirects the merchant back
  // to /auth/square/callback?code=...&state=..., the callback handler
  // exchanges the code for tokens via the square-oauth edge function,
  // then bounces back to /. Vercel's SPA fallback serves index.html
  // for any unknown path, so this just-works without router config.
  if (typeof window !== 'undefined' && window.location.pathname === '/auth/square/callback') {
    return <SquareCallbackHandler />
  }

  // Authed tree. UnitProvider owns "which unit is in scope" and is inert
  // (single mode, currentRestaurantId=null) for all existing single-unit
  // accounts — guaranteeing zero behavior change for them.
  return (
    <UnitProvider>
      <AuthedApp session={session} />
    </UnitProvider>
  )
}

// AuthedApp — everything that renders once a session exists. Split out
// of App so it lives INSIDE UnitProvider and can call useCurrentUnit().
function AuthedApp({ session }) {
  const [activeTab, setActiveTab]     = useState('huddle')
  const [stockSub, setStockSub]       = useState('receive')
  const [scheduleSub, setScheduleSub] = useState('roster')
  const [postBillingToast, setPostBillingToast] = useState('')
  const { isOperator } = useIsOperator()
  // Multi-unit scope. For single-unit accounts: mode==='single',
  // isGrouped===false, so the UnitSwitcher renders nothing and the
  // all_units overlay never engages.
  const { mode, currentGroupId, isGrouped, allUnits, switchUnit, loading: unitsLoading } = useCurrentUnit()
  // Operator Queue is a hidden screen, opened via ?op=queue (the link
  // in the operator notification email goes here) or via the small
  // "Operator queue" link that renders in the header for operators.
  // It deliberately doesn't live in the main TabBar — most operators
  // are also customers, and we don't want to clutter their daily nav.
  const [operatorOpen, setOperatorOpen] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('op') === 'queue') {
      setOperatorOpen(true)
      const url = new URL(window.location.href)
      url.searchParams.delete('op')
      window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams}` : '') + url.hash)
    }
  }, [])

  // Stripe Checkout redirects back to /?billed=ok&session=… on success
  // or /?billed=cancel if the user backed out. Detect that on first
  // render, jump to the Settings tab so the SubscriptionCard is the
  // first thing they see, and surface a toast confirming what happened.
  // We also clean the URL with replaceState so a refresh doesn't
  // re-trigger the toast.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const billed = params.get('billed')
    if (!billed) return
    if (billed === 'ok') {
      setActiveTab('settings')
      setPostBillingToast('Subscription started — welcome aboard!')
    } else if (billed === 'cancel') {
      setPostBillingToast('Checkout canceled. You can pick a plan any time.')
    }
    // Strip the query string so re-renders / refreshes don't replay this.
    const url = new URL(window.location.href)
    url.searchParams.delete('billed')
    url.searchParams.delete('session')
    window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams}` : '') + url.hash)
  }, [])

  // Morning-brief push deep-link. The send-morning-brief edge fn pushes a
  // url of the form /?brief=<key>&unit=<restaurant_id>; tapping it should land
  // the operator on the relevant action surface (reorder / waste) with the
  // brief's unit selected. NOTIFY-ONLY — pure navigation, no execute.
  //
  // Timing: `allUnits` loads async (unitsLoading), and we only know whether a
  // unit id is valid for THIS operator once it's populated. So we (1) capture
  // the parsed params ONCE at boot into a ref (they survive the op/billed
  // strippers, which don't touch brief/unit), then (2) wait for !unitsLoading
  // to apply the unit switch (grouped operators only; single-unit operators
  // have one inert unit so the switch is a no-op and is skipped) and the
  // navigation, then strip the params so a refresh doesn't replay. A ref gate
  // (deepLinkRef.current.done) guarantees it runs at most once.
  const deepLinkRef = useRef({ parsed: false, done: false, brief: null, unit: null })
  useEffect(() => {
    if (typeof window === 'undefined') return
    const ref = deepLinkRef.current
    if (ref.done) return

    // (1) Capture once — before anything could strip the params.
    if (!ref.parsed) {
      const { brief, unit } = parseBriefDeepLink(window.location.search)
      ref.brief = brief
      ref.unit = unit
      ref.parsed = true
      // Nothing to do (no brief and no unit) → mark done, never re-run.
      if (!brief && !unit) {
        ref.done = true
        return
      }
    }

    // (2) Wait until the unit list has loaded so a unit id can be validated.
    if (unitsLoading) return

    // Select the brief's unit for grouped operators when the id is one of
    // theirs. Single-unit operators (!isGrouped) have one inert unit, so the
    // switch is skipped. A stale/foreign id (not in allUnits) is ignored.
    if (ref.unit && isGrouped && allUnits.some((u) => u.id === ref.unit)) {
      switchUnit(ref.unit)
    }

    // Navigate to the action surface. parseBriefDeepLink already validated
    // `brief` against the navigateTo keys, so unknown values are null here.
    if (ref.brief) {
      navigateTo(ref.brief)
    }

    // Strip brief/unit so a refresh doesn't replay this.
    const url = new URL(window.location.href)
    url.searchParams.delete('brief')
    url.searchParams.delete('unit')
    window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams}` : '') + url.hash)

    ref.done = true
  }, [unitsLoading, isGrouped, allUnits])

  // All-Units roll-up overlay (mirrors operatorOpen). Drive a vanity URL
  // when the roll-up is active so it reads as /g/<group_id>/dashboard;
  // Vercel's SPA fallback serves index.html for the unknown path. Reset
  // to / on exit. Single-unit users never reach mode==='all_units'.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (mode === 'all_units' && currentGroupId) {
      window.history.replaceState({}, '', `/g/${currentGroupId}/dashboard`)
    } else if (window.location.pathname.startsWith('/g/')) {
      window.history.replaceState({}, '', '/')
    }
  }, [mode, currentGroupId])

  // Entering the All-Units roll-up lands on the roll-up home (huddle).
  // Depends only on `mode`, so it fires on the transition INTO all_units
  // and is idempotent; the operator can still tap a per-unit tab
  // afterward (which then shows the SelectUnitPrompt).
  useEffect(() => {
    if (mode === 'all_units') setActiveTab('huddle')
  }, [mode])

  // Single navigation entry point. Accepts either a top-level tab id
  // OR an old/sub id (waste, receive, reorder, etc.) and routes to
  // the right (tab, sub) pair. Keeps existing Huddle quick-link calls
  // working without churn at the call sites.
  function navigateTo(id) {
    const map = {
      huddle:    { tab: 'huddle' },
      // Stock hub + its subs
      stock:     { tab: 'stock' },
      receive:   { tab: 'stock', sub: 'receive' },
      reorder:   { tab: 'stock', sub: 'reorder' },
      stocktake: { tab: 'stock', sub: 'stocktake' },
      variance:  { tab: 'stock', sub: 'variance' },
      waste:     { tab: 'stock', sub: 'waste' },
      // Schedule hub + its subs
      schedule:  { tab: 'schedule' },
      roster:    { tab: 'schedule', sub: 'roster' },
      catering:  { tab: 'schedule', sub: 'catering' },
      // Morning-brief labor (schedule overtime) action → the roster, where the
      // approve-and-execute card renders.
      labor:     { tab: 'schedule', sub: 'roster' },
      // Standalone tabs
      insights:  { tab: 'insights' },
      marketing: { tab: 'marketing' },
      settings:  { tab: 'settings' },
    }
    const target = map[id]
    if (!target) return
    setActiveTab(target.tab)
    // Clicking any top-level tab also dismisses the operator queue
    // overlay, otherwise navigating away would leave the queue stuck
    // on screen with the tab highlighted but the wrong content visible.
    setOperatorOpen(false)
    if (target.sub) {
      if (target.tab === 'stock')    setStockSub(target.sub)
      if (target.tab === 'schedule') setScheduleSub(target.sub)
    }
  }

  // The sub-tab pills shown for whichever hub is active. Lifted into
  // the sticky header so they stick along with the top-level tabs.
  // Suppressed in the all-units roll-up: there the per-unit tabs render
  // the "select a unit" prompt, so a hub's sub-tab pills would highlight
  // but do nothing.
  let headerSubNav = null
  if (mode !== 'all_units' && activeTab === 'stock') {
    headerSubNav = (
      <SubTabPills items={STOCK_SUBS} active={stockSub} onChange={setStockSub} ariaLabel="Stock sections" />
    )
  } else if (mode !== 'all_units' && activeTab === 'schedule') {
    headerSubNav = (
      <SubTabPills items={SCHEDULE_SUBS} active={scheduleSub} onChange={setScheduleSub} ariaLabel="Schedule sections" />
    )
  }

  return (
    <div className="min-h-full">
      {/* Shared amber-brand header — sticky so it persists during scroll.
          Includes the top-level TabBar and the active hub's sub-tab
          pill nav (when applicable), so both layers of nav remain
          visible as the manager scrolls inside a sub-screen. */}
      <header className="sticky top-0 z-30 bg-amber-brand-50 text-amber-brand-800 shadow-sm safe-top">
        <div className="mx-auto max-w-screen-sm px-4 pt-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/logo.png"
                alt=""
                className="h-7 w-7 rounded-md shadow-sm flex-shrink-0"
              />
              <div className="text-xs font-medium opacity-90 truncate">
                AI Restaurant Manager
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Multi-unit switcher — renders nothing for single-unit
                  accounts (the core regression guarantee). */}
              <UnitSwitcher />
              {isOperator && (
                <button
                  type="button"
                  onClick={() => setOperatorOpen(true)}
                  className="rounded-md bg-amber-brand-200/40 px-2 py-0.5 text-[11px] font-medium hover:bg-amber-brand-200/60"
                  title="Operator Queue (managed-posting)"
                >
                  🛠 Queue
                </button>
              )}
              {session.user?.email && (
                <div className="truncate text-[11px] opacity-75">
                  {session.user.email}
                </div>
              )}
            </div>
          </div>
        </div>
        <TabBar active={activeTab} onChange={navigateTo} />
        {headerSubNav}
      </header>

      {operatorOpen && isOperator ? (
        // Operator queue takes precedence over the All-Units overlay so a
        // grouped operator can always reach the queue (e.g. via ?op=queue)
        // regardless of the current unit-switcher selection.
        <div className="pb-4">
          <div className="mx-4 mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => setOperatorOpen(false)}
              className="text-xs text-blue-700 hover:underline"
            >
              ← Back to app
            </button>
          </div>
          <OperatorQueueScreen />
        </div>
      ) : mode === 'all_units' ? (
        // All-Units roll-up overlay. The roll-up home (huddle tab) shows
        // the GroupDashboardScreen — every group surface is reached via
        // its own internal nav. Per-unit tabs are scoped to one
        // restaurant, so they show a prompt to pick a unit; choosing one
        // flips mode back to 'group' and returns to normal tab content.
        <>
          {activeTab === 'huddle' ? (
            <GroupDashboardScreen />
          ) : activeTab === 'stock' ? (
            <SelectUnitPrompt action="receive a delivery or manage inventory" />
          ) : activeTab === 'schedule' ? (
            <SelectUnitPrompt action="manage the schedule" />
          ) : activeTab === 'insights' ? (
            <SelectUnitPrompt action="view weekly insights" />
          ) : activeTab === 'marketing' ? (
            <SelectUnitPrompt action="manage marketing" />
          ) : activeTab === 'settings' ? (
            <SelectUnitPrompt action="change settings" />
          ) : (
            <GroupDashboardScreen />
          )}
        </>
      ) : (
        <>
          {activeTab === 'huddle'    && <DailyHuddleScreen onSwitchTab={navigateTo} />}
          {activeTab === 'stock'     && <InventoryHubScreen sub={stockSub} />}
          {activeTab === 'schedule'  && <ScheduleHubScreen sub={scheduleSub} />}
          {activeTab === 'insights'  && <WeeklyInsightsScreen />}
          {activeTab === 'marketing' && <PromotionsScreen />}
          {activeTab === 'settings'  && <SettingsScreen />}
        </>
      )}

      {/* Post-billing toast — fires once after the Stripe Checkout
          success/cancel redirect lands on /?billed=ok|cancel. */}
      <Toast message={postBillingToast} onDone={() => setPostBillingToast('')} />
    </div>
  )
}

// Sub-tab pill nav, styled for the amber-brand header context.
// Active pill is a solid white capsule with amber-brand-800 text —
// the only pill with a visible white background, so the active state
// is unambiguous at a glance. Inactive pills sit in a recessed
// amber-200 background that still contrasts with the amber-50
// header surface but reads as tappable. Horizontally scrolls if the
// labels ever overflow the viewport.
function SubTabPills({ items, active, onChange, ariaLabel }) {
  return (
    <nav
      aria-label={ariaLabel}
      className="
        mx-auto flex max-w-screen-sm gap-2 overflow-x-auto
        px-4 pb-2
        [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
      "
    >
      {items.map((item) => {
        const isActive = active === item.id
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onChange(item.id)}
            aria-current={isActive ? 'page' : undefined}
            className={`
              shrink-0 whitespace-nowrap rounded-full px-3.5 py-1
              text-xs font-medium transition
              ${isActive
                ? 'bg-white text-amber-brand-800 shadow'
                : 'bg-amber-brand-200/50 text-amber-brand-800/85 hover:bg-amber-brand-200/80 hover:text-amber-brand-900'}
            `}
          >
            {item.label}
          </button>
        )
      })}
    </nav>
  )
}
